import React, { useState, useEffect, useContext } from 'react';
import { Switch, useParams, useRouteMatch, Route, Redirect, useHistory } from 'react-router-dom';
import {
  getConversationSurvey,
  setConversationSurveySource,
  updateConversationSurvey,
} from '../../../api/conversation_surveys';
import { isFeatureEnabled, accountFromConversationSurvey, FEATURES } from '../../../helpers/feature_checker';

import LoadingScreen from '../../system/loading_screen';
import Generic from './generic';
import Bylvay from './bylvay/index_gated.jsx';
import BylvayThankYou from './bylvay/thank_you.jsx';
import PostSurveyThankYou from '../post_survey_thank_you/index_gated.jsx';
import DynamicQuestionnaire from '../dynamic';
import Context from '../../../utils/context';
import SurveyExitContext from '../../../utils/survey_exit_context';
import UserMenu from '../../chrome/admin_header/user_menu';

import styles from '../questionnaire.module.css';

const THANK_YOU_COMPONENTS = {
  bylvay: BylvayThankYou,
};

const EXPLICIT_COMPLETION_SOURCES = ['banner', 'in_app_modal', 'in_app_notification'];

// Maps the entry point (an explicit ?source= from the banner/modal/notification,
// or the utm_source carried by the SMS/email links) to a canonical completion source.
const resolveCompletionSource = (search) => {
  const params = new URLSearchParams(search || '');

  const explicit = params.get('source');
  if (EXPLICIT_COMPLETION_SOURCES.includes(explicit)) {
    return explicit;
  }

  const utmSource = params.get('utm_source') || '';
  if (utmSource.startsWith('PostSurveySMS')) {
    return 'sms';
  }
  if (utmSource.startsWith('PostSurveyEmail')) {
    return 'email';
  }

  return null;
};

export default function PostConversationQuestionnaire() {
  const context = useContext(Context);
  const [conversationSurvey, setConversationSurvey] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { conversationSurveyId } = useParams();

  let history = useHistory();
  let { path } = useRouteMatch();
  path = path.replace(':conversationSurveyId', conversationSurveyId);
  const completionSource = resolveCompletionSource(history?.location?.search);

  useEffect(() => {
    new Promise((resolve) => {
      getConversationSurvey(conversationSurveyId, (data) => {
        setConversationSurvey(data);

        // Record how the mentee reached the survey (US-004) so a completion is
        // attributed to the banner, in-app modal/notification, SMS, or email.
        if (completionSource && data && !data.completed && !context?.auth?.isSuperUser?.()) {
          setConversationSurveySource(conversationSurveyId, completionSource);
        }

        resolve();
      });
    }).finally(() => {
      setIsLoading(false);
    });
  }, [conversationSurveyId, completionSource, history, path]);

  // A builder survey's done state is its end rule. Home leaves for the
  // conversations list; the page-level thank-you belongs to the hardcoded flows.
  const goToConversations = () => {
    history.push('/member/conversations');
  };

  // Every save carries the entry point. Completing the survey is a different
  // request from the one fired on load, and that request is what has to record
  // the method if the load-time call has not landed yet.
  const withCompletionSource = (params) => {
    if (!completionSource || context?.auth?.isSuperUser?.()) return params;

    return { ...params, completion_source: completionSource };
  };

  const update = (params) => {
    updateConversationSurvey(conversationSurveyId, withCompletionSource(params), (data) => setConversationSurvey(data));
  };

  const answeredFinalQuestion = (params) => {
    update({ ...params, completed: true });
  };

  // This is for actions that need to update the servey without triggering any events.
  const completeSurvey = (params) => {
    updateConversationSurvey(conversationSurveyId, withCompletionSource({ ...params, completed: true }), () => {});
  };

  const renderAllQuestionnaires = () => {
    if (conversationSurvey?.completed && conversationSurvey.questionnaire_response_uuid) {
      return <Redirect to='/member/conversations' />;
    }

    if (!conversationSurvey || conversationSurvey.completed) {
      const CustomThankYou = THANK_YOU_COMPONENTS[conversationSurvey?.account_slug];
      if (CustomThankYou) {
        return <CustomThankYou conversationSurvey={conversationSurvey} />;
      }
      return <PostSurveyThankYou conversationSurvey={conversationSurvey} />;
    }

    // Surveys the account authored in the questionnaire builder run through the
    // dynamic flow rather than a component picked from the account slug. The
    // flow reads and answers through the conversation-survey endpoints (keyed by
    // the survey uuid), so a member arriving from an emailed or texted link
    // keeps working while logged out, the same way legacy surveys do.
    if (conversationSurvey.questionnaire_response_uuid) {
      return <DynamicQuestionnaire conversationSurveyUuid={conversationSurveyId} onFinish={goToConversations} />;
    }

    const SURVEY_COMPONENTS = {
      bylvay: Bylvay,
      default: Generic,
    };

    const SurveyComponent = SURVEY_COMPONENTS[conversationSurvey.account_slug] || SURVEY_COMPONENTS.default;

    const commonProps = {
      path: `${path}`,
      update,
      answeredFinalQuestion,
      completeSurvey,
      history,
      conversationSurvey,
    };

    return (
      <Switch>
        <Route path={`${path}`}>
          <SurveyComponent {...commonProps} />
        </Route>
      </Switch>
    );
  };

  // Lets the patient leave the survey from the top bar. Logged-in patients go
  // back to the conversation that prompted the survey; unauthenticated patients
  // (who reach the survey via an emailed link) are sent to the login page.
  const handleExitSurvey = () => {
    const isLoggedIn = context?.auth?.isLoggedIn?.();
    if (isLoggedIn && conversationSurvey?.conversation_uuid) {
      history.push(`/member/conversations/${conversationSurvey.conversation_uuid}`);
    } else {
      history.push('/member/login');
    }
  };

  const body = isLoading ? <LoadingScreen /> : renderAllQuestionnaires();
  const authAccount = context?.auth?.currentAccount?.();
  const accountForFeatureCheck = authAccount || accountFromConversationSurvey(conversationSurvey);
  const newUiEnabled = isFeatureEnabled(accountForFeatureCheck, FEATURES.NEW_UI);

  const content = newUiEnabled ? (
    body
  ) : (
    <div className={styles.questionnaire_ottr}>
      <div className={styles.legacy_exit_bar}>
        <button type='button' className={styles.legacy_exit_button} onClick={handleExitSurvey}>
          Exit
        </button>
        <UserMenu />
      </div>
      <div className={`${styles.questionnaire_content}`}>
        <div className={`${styles.cont_row}`}>{body}</div>
      </div>
    </div>
  );

  return <SurveyExitContext.Provider value={{ onExit: handleExitSurvey }}>{content}</SurveyExitContext.Provider>;
}
